import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Settings, Sparkles, AlertCircle, ShoppingCart, MessageSquare, BarChart3, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildUrlWithOrg } from '@/shared/lib/brandUtils';
import { useNavigation } from '@/shared/hooks/useNavigation';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { isSavChatEnabled, isLiveAssistantEnabled } from '@/shared/config/featureFlags';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { useOrgaConfigurationContext } from '@/app/providers';
import { useSavChats } from '@/features/admin/hooks/useSavChats';

interface AdminSidebarProps {
  className?: string;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { organizationId: orgIdFromHook } = useNavigation();

  // Extraire organizationId de l'URL de manière réactive
  // MUST be computed BEFORE useOrgaConfiguration to pass it as parameter
  const organizationId = React.useMemo(() => {
    // Essayer d'abord useNavigation
    if (orgIdFromHook) {
      return orgIdFromHook;
    }
    // Fallback: extraire directement de l'URL
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments.length >= 2 && pathSegments[0] === "orga") {
      return pathSegments[1] || "";
    }
    return "";
  }, [orgIdFromHook, location.pathname]);

  // Now use the shared context instead of initialization a new listener
  const { configuration, completionStatus } = useOrgaConfigurationContext();
  const { isPortalReady } = completionStatus;

  const { canManageConfiguration, permissionsLoading } = usePermissionsHook();

  // Charger les stats SAV pour l'alerte
  const { stats: savStats } = useSavChats(organizationId || null);

  // Log initial
  React.useEffect(() => {
    console.log('🔍 AdminSidebar - Initialisation:', {
      organizationId,
      orgIdFromHook,
      permissionsLoading,
      hasCanManageConfig: typeof canManageConfiguration === 'function',
      location: location.pathname,
      pathSegments: location.pathname.split("/").filter(Boolean)
    });
  }, [organizationId, orgIdFromHook, permissionsLoading, canManageConfiguration, location.pathname]);

  // Chemins utiles + détection actif
  const orgBase = organizationId ? `/orga/${organizationId}` : '';
  const pathAdmin = `${orgBase}/admin`;
  const pathConfig = `${orgBase}/admin/configuration`;
  const pathLiveAssistant = `${orgBase}/admin/live-assistant`;
  const pathSav = `${orgBase}/admin/sav`;
  const pathAnalytics = `${orgBase}/admin/analytics`;
  const pathCustomers = `${orgBase}/admin/customers`;
  // Fonction pour vérifier si un chemin est actif
  // Pour le tableau de bord, on veut être strict : seulement /admin ou /admin/ (sans sous-routes)
  const isActivePath = (p: string, exact: boolean = false) => {
    if (exact) {
      // Pour le tableau de bord : exactement /admin ou /admin/
      return location.pathname === p || location.pathname === `${p}/`;
    }
    // Pour les autres routes : correspondance exacte ou sous-route
    return location.pathname === p || location.pathname.startsWith(`${p}/`);
  };

  // Vérifier les permissions - utiliser useMemo pour recalculer quand organizationId ou canManageConfiguration change
  const hasConfigPermission = React.useMemo(() => {
    console.log('🔍 AdminSidebar - useMemo hasConfigPermission - Début calcul:', {
      organizationId,
      permissionsLoading,
      hasCanManageConfig: typeof canManageConfiguration === 'function'
    });

    // Ne pas vérifier les permissions si elles sont encore en chargement
    if (permissionsLoading) {
      console.log('⏳ AdminSidebar - Permissions en chargement, retour false');
      return false;
    }

    if (!organizationId) {
      console.log('❌ AdminSidebar - Pas d\'organizationId, retour false');
      return false;
    }

    try {
      const permission = canManageConfiguration(organizationId);
      console.log('✅ AdminSidebar - Calcul hasConfigPermission:', {
        organizationId,
        permission,
        permissionsLoading,
        timestamp: new Date().toISOString()
      });
      return permission;
    } catch (error) {
      console.error('❌ AdminSidebar - Erreur lors de l\'appel à canManageConfiguration:', error);
      return false;
    }
  }, [organizationId, canManageConfiguration, permissionsLoading]);

  // Debug: Log des permissions et du menu
  React.useEffect(() => {
    console.log('🔍 AdminSidebar - État complet:', {
      organizationId,
      hasConfigPermission,
      permissionsLoading,
      canManageConfigType: typeof canManageConfiguration,
      timestamp: new Date().toISOString()
    });
  }, [organizationId, hasConfigPermission, permissionsLoading, canManageConfiguration]);

  // Obtenu via useOrgaConfigurationContext en haut du composant
  // const { isPortalReady } = completionStatus;

  // Utiliser le thème choisi par le client via ThemeContext
  const { theme } = useTheme();

  // Couleur primaire du thème choisi par le client
  const getActiveIconColor = React.useMemo(() => {
    // Priorité 1: Couleur du thème ThemeContext (choix du client)
    if (theme?.colors?.primary) {
      return theme.colors.primary;
    }

    // Fallback: Utiliser la variable CSS --primary
    return 'var(--primary)';
  }, [theme?.colors?.primary]);

  // Construire le menu avec les permissions - utiliser useMemo pour éviter les recalculs
  const menuItems = React.useMemo(() => {
    console.log('🔍 AdminSidebar - Construction du menu:', {
      organizationId,
      hasConfigPermission,
      permissionsLoading,
      isPortalReady
    });

    const items = [
      {
        name: t('admin.sidebar.dashboard'),
        shortName: t('admin.sidebar.dashboardShort'),
        description: t('admin.sidebar.dashboardDesc'),
        href: buildUrlWithOrg('/admin', organizationId),
        icon: Shield,
        isActive: isActivePath(pathAdmin, true), // exact = true pour éviter d'être actif sur /admin/configuration
        alert: false
      }
    ];

    // Ajouter Analytics pour les admins
    if (hasConfigPermission) {
      items.push({
        name: t('admin.sidebar.analytics'),
        shortName: t('admin.sidebar.analyticsShort'),
        description: t('admin.sidebar.analyticsDesc'),
        href: buildUrlWithOrg('/admin/analytics', organizationId),
        icon: BarChart3,
        isActive: isActivePath(pathAnalytics),
        alert: false
      });

      // Ajouter Clients pour les admins
      items.push({
        name: t('admin.sidebar.customers'),
        shortName: t('admin.sidebar.customersShort'),
        description: t('admin.sidebar.customersDesc'),
        href: buildUrlWithOrg('/admin/customers', organizationId),
        icon: Users,
        isActive: isActivePath(pathCustomers),
        alert: false
      });
    }

    // Ajouter Live Assistant uniquement si l'utilisateur est admin ET si la fonctionnalité est activée pour cette organisation
    if (hasConfigPermission && isLiveAssistantEnabled(organizationId, configuration)) {
      items.push({
        name: t('admin.sidebar.liveAssistant'),
        shortName: t('admin.sidebar.liveAssistantShort'),
        description: t('admin.sidebar.liveAssistantDesc'),
        href: buildUrlWithOrg('/admin/live-assistant', organizationId),
        icon: Sparkles,
        isActive: isActivePath(pathLiveAssistant),
        alert: false
      });
    }

    // Ajouter SAV / Support uniquement pour les admins ET si la fonctionnalité est activée pour cette organisation
    if (hasConfigPermission && isSavChatEnabled(organizationId, configuration)) {
      items.push({
        name: t('admin.sidebar.sav'),
        shortName: t('admin.sidebar.savShort'),
        description: t('admin.sidebar.savDesc'),
        href: buildUrlWithOrg('/admin/sav', organizationId),
        icon: MessageSquare,
        isActive: isActivePath(pathSav),
        alert: savStats.unreadCount > 0 || savStats.open > 0
      });
    }

    // Ajouter Configuration uniquement si l'utilisateur a la permission
    console.log('🔍 AdminSidebar - Vérification condition Config:', {
      hasConfigPermission,
      type: typeof hasConfigPermission,
      value: hasConfigPermission
    });

    if (hasConfigPermission) {
      console.log('✅ AdminSidebar - Ajout de la section Config au menu');
      items.push({
        name: t('admin.sidebar.configuration'),
        shortName: t('admin.sidebar.configurationShort'),
        description: t('admin.sidebar.configurationDesc'),
        href: buildUrlWithOrg('/admin/configuration', organizationId),
        icon: Settings,
        isActive: isActivePath(pathConfig),
        alert: !isPortalReady
      });
    } else {
      console.log('❌ AdminSidebar - Section Config NON ajoutée (hasConfigPermission = false)');
    }

    // Lien vers le formulaire de commande (Order Form)
    items.push({
      name: t('admin.sidebar.portail'),
      shortName: t('admin.sidebar.portailShort'),
      description: t('admin.sidebar.portailDesc'),
      href: buildUrlWithOrg('/order', organizationId),
      icon: ShoppingCart,
      isActive: isActivePath(`${orgBase}/order`),
      alert: false
    });

    console.log('🔍 AdminSidebar - Menu final:', {
      itemsCount: items.length,
      itemsNames: items.map(i => i.name),
      hasConfig: items.some(i => i.name === 'Configuration')
    });

    return items;
  }, [organizationId, hasConfigPermission, pathAdmin, pathConfig, pathLiveAssistant, isActivePath, orgBase, isPortalReady, configuration?.paymentMode]);

  return (
    <aside
      id="logo-sidebar"
      className={`hidden md:block fixed left-0 top-16 z-40 w-16 bg-background-secondary border-r border-border ${className}`}
      style={{
        height: 'calc(100vh - 64px)'
      }}
      aria-label="Sidebar"
    >
      {/* Menu compact: icônes centrées + mini libellé, pas de scroll */}
      <div className="h-full flex flex-col">
        <nav className="flex-1 px-2 pt-8 pb-4 space-y-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const hasAlert = (item as any).alert;
            // Get the active color for this item
            const activeColor = getActiveIconColor || 'var(--primary)';

            return (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  aria-label={item.name}
                  className={`relative flex justify-center items-center flex-col rounded-lg px-3 py-2 transition-all duration-200 ${item.isActive ? 'bg-background-tertiary text-foreground' : 'text-foreground-secondary hover:text-foreground hover:bg-background-tertiary'
                    }`}
                >
                  <div className="relative">
                    <Icon
                      className="h-5 w-5"
                      style={item.isActive ? { color: activeColor } : undefined}
                    />
                    {hasAlert && (
                      <div className="absolute -top-1 -right-1 bg-background-secondary rounded-full">
                        <AlertCircle className="h-3 w-3 text-warning" />
                      </div>
                    )}
                  </div>
                  <span
                    className="mt-1 text-[10px] leading-none max-w-[2.75rem] text-center truncate"
                    style={item.isActive ? { color: activeColor } : { color: 'var(--foreground-secondary)' }}
                  >
                    {('shortName' in item && (item as any).shortName) || ''}
                  </span>
                </Link>

                {/* Tooltip */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-background-tertiary text-foreground text-xs px-3 py-1.5 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  <div className="font-medium">{item.name}</div>
                  {('description' in item && (item as any).description) && (
                    <div className="text-[11px] text-foreground-secondary">{(item as any).description}</div>
                  )}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-background-tertiary" />
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default AdminSidebar;
